// Extend Tabulator's range selection with edge scrolling and a resize handle.
export default class RangeSelection {
  constructor (table, grid) {
    this.table = table
    this.grid = grid
    this.holder = grid.querySelector('.tabulator-tableholder')
    this.handle = document.createElement('button')
    this.handle.type = 'button'
    this.handle.className = 'report-tool-range-handle'
    this.handle.setAttribute('aria-label', 'Drag to extend selection')
    this.handle.title = 'Drag to extend selection'
    this.handle.tabIndex = -1
    this.mouseDown = event => this.start(event)
    this.mouseMove = event => {
      this.pointer = { x: event.clientX, y: event.clientY }
    }
    this.mouseUp = () => this.stop()
    this.grid.addEventListener('mousedown', this.mouseDown, true)
    this.renderHandle = () => {
      if (this.destroyed || this.handleFrame) return
      this.handleFrame = requestAnimationFrame(() => {
        this.handleFrame = null
        const range = this.table.getRanges()[0]
        if (range?.getRows().length) range.getElement().append(this.handle)
        else this.handle.remove()
      })
    }
    // The default first-cell range can change without a range notification.
    // Refresh from cell interactions too, including a click without dragging.
    for (const event of [
      'rangeAdded',
      'rangeChanged',
      'rangeRemoved',
      'dataFiltered',
      'cellMouseDown',
      'cellMouseUp',
      'cellClick',
      'headerMouseUp'
    ]) { table.on(event, this.renderHandle) }
    table.on('columnMoving', this.mouseUp)
    table.on('tableDestroyed', () => this.destroy())
    this.renderHandle()
  }

  start (event) {
    if (
      !event.isTrusted ||
      event.button !== 0 ||
      event.target.closest('.tabulator-col-resize-handle')
    ) { return }
    const resizing = event.target === this.handle
    if (!resizing && !event.target.closest('.tabulator-cell, .tabulator-col')) return
    this.stop()
    this.pointer = { x: event.clientX, y: event.clientY }
    this.renderHandle()
    this.columnDrag = !resizing && !!event.target.closest('.tabulator-col')
    this.rowDrag = !resizing && !!event.target.closest('.tabulator-range-row-header')
    if (resizing) {
      event.preventDefault()
      event.stopPropagation()
      this.range = this.table.getRanges()[0]
      const cells = this.range.getCells()
      this.anchor = cells[0][0]
      // Switch header selections to cell selection through Tabulator's normal
      // shift-drag interaction, preserving the current bounds.
      cells
        .at(-1)
        .at(-1)
        .getElement()
        .dispatchEvent(new MouseEvent('mousedown', { bubbles: true, button: 0, shiftKey: true }))
      this.handle.style.pointerEvents = 'none'
    }
    document.addEventListener('mousemove', this.mouseMove)
    document.addEventListener('mouseup', this.mouseUp)
    window.addEventListener('blur', this.mouseUp)
    this.frame = requestAnimationFrame(time => this.drag(time))
  }

  drag (time) {
    const rect = this.holder.getBoundingClientRect()
    const frozen = this.grid.querySelector('.tabulator-row-header')?.offsetWidth || 0
    const left = rect.left + frozen
    const right = rect.left + this.holder.clientWidth
    const bottom = rect.top + this.holder.clientHeight
    const speed = (value, min, max) =>
      value < min + 28
        ? -Math.min(1, (min + 28 - value) / 28)
        : value > max - 28
          ? Math.min(1, (value - max + 28) / 28)
          : 0
    const step = Math.min(time - (this.lastTime || time), 32) * 0.6
    this.lastTime = time
    const previousLeft = this.holder.scrollLeft
    const previousTop = this.holder.scrollTop
    if (!this.rowDrag) this.holder.scrollLeft += speed(this.pointer.x, left, right) * step
    if (!this.columnDrag) this.holder.scrollTop += speed(this.pointer.y, rect.top, bottom) * step
    const scrolled =
      previousLeft !== this.holder.scrollLeft || previousTop !== this.holder.scrollTop
    const x = Math.max(rect.left + 2, Math.min(this.pointer.x, right - 2))
    const y = this.columnDrag
      ? this.pointer.y
      : Math.max(rect.top + 2, Math.min(this.pointer.y, bottom - 2))
    const target = document.elementFromPoint(x, y)
    if (this.range) {
      const cell = target?.closest('.tabulator-cell')
      if (cell && this.grid.contains(cell)) {
        const row = this.table.getRow(cell.closest('.tabulator-row'))
        const field = cell.getAttribute('tabulator-field')
        if (row && field && cell !== this.lastCell) {
          this.range.setBounds(this.anchor, row.getCell(field))
          this.lastCell = cell
        }
      }
    } else if ((scrolled || this.scrolled) && target && this.grid.contains(target)) {
      // Virtual rows may render a frame after scrolling. Replay the pointer at
      // the visible edge so Tabulator extends the range to newly visible cells.
      target.dispatchEvent(
        new MouseEvent('mousemove', { bubbles: true, clientX: x, clientY: y, buttons: 1 })
      )
    }
    this.scrolled = scrolled
    this.renderHandle()
    this.frame = requestAnimationFrame(next => this.drag(next))
  }

  stop () {
    cancelAnimationFrame(this.frame)
    this.frame = null
    this.lastTime = null
    this.lastCell = null
    this.scrolled = false
    this.range = null
    this.anchor = null
    this.handle.style.pointerEvents = ''
    document.removeEventListener('mousemove', this.mouseMove)
    document.removeEventListener('mouseup', this.mouseUp)
    window.removeEventListener('blur', this.mouseUp)
    this.renderHandle()
  }

  destroy () {
    this.destroyed = true
    cancelAnimationFrame(this.handleFrame)
    this.stop()
    this.handle.remove()
    this.grid.removeEventListener('mousedown', this.mouseDown, true)
  }
}
